import { useEffect, useState } from "react";
import {
  Building2, Crown, Loader2, Pencil, Plus, Save, Trash2,
  Users as UsersIcon, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useCreateDepartment,
  useDeleteDepartment,
  useDepartments,
  useUpdateDepartment,
  type Department,
} from "@/hooks/useUserMutations";
import { useUsers } from "@/hooks/useUsers";

/**
 * /departments — CEO+ surface for managing departments. List on the
 * left, "New department" button up top, edit/delete on row hover.
 * Each row shows a member count so admins can spot empty (safe to
 * remove) or oversized departments at a glance.
 */
export default function Departments() {
  const list    = useDepartments();
  const del     = useDeleteDepartment();
  const confirm = useConfirm();

  const [creating, setCreating] = useState(false);
  const [editing,  setEditing]  = useState<Department | null>(null);

  async function onDelete(d: Department) {
    const members = d.member_count ?? 0;
    const description = members > 0
      ? `It currently has ${members} member${members === 1 ? "" : "s"} — they'll be moved to no department.`
      : "This cannot be undone.";
    const ok = await confirm({
      title: `Delete "${d.name}"?`,
      description,
      confirmLabel: "Delete department",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(d.id);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Departments</h1>
            <p className="text-sm text-muted-foreground">
              Organisational units. Directors are scoped to their own department;
              CEO and Admin can move people between them.
            </p>
          </div>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />New department
        </Button>
      </header>

      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : list.data && list.data.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {list.data.map((d) => (
                <li
                  key={d.id}
                  className="group flex items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/30 sm:px-5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold">{d.name}</span>
                      <Badge variant="outline" className="font-mono tabular-nums">
                        <UsersIcon className="mr-1 h-3 w-3" />
                        {d.member_count ?? 0}
                      </Badge>
                      {d.ceo_name ? (
                        <Badge
                          variant="outline"
                          className="border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 font-medium text-amber-800 dark:text-amber-300"
                          title="CEO leading this department"
                        >
                          <Crown className="mr-1 h-3 w-3 fill-amber-400 text-amber-600" />
                          {d.ceo_name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-dashed text-muted-foreground">
                          <Crown className="mr-1 h-3 w-3" />no CEO
                        </Badge>
                      )}
                    </div>
                    {d.description && (
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {d.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => setEditing(d)}>
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Edit {d.name}</span>
                    </Button>
                    <Button variant="ghost" size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={del.isPending}
                      onClick={() => onDelete(d)}>
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete {d.name}</span>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">No departments yet</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Add the first one — directors and users get scoped to a department.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <DepartmentFormDialog
        open={creating || editing !== null}
        onClose={() => { setCreating(false); setEditing(null); }}
        department={editing}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create + edit dialog — shared shell, decides mode from the `department`
// prop. Two-field form (name + description) keeps it dead simple.
// ---------------------------------------------------------------------------
function DepartmentFormDialog({
  open, onClose, department,
}: {
  open: boolean;
  onClose: () => void;
  department: Department | null;
}) {
  const isEdit = !!department;
  const create = useCreateDepartment();
  const update = useUpdateDepartment();
  const mutation = isEdit ? update : create;
  const { data: actor } = useCurrentUser();

  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [ceoId,       setCeoId]       = useState<string | null>(null);

  // CEO picker — Admins only (backend gates ceo_id writes too).
  // We list every active CEO; the picker shows them all so the admin
  // can reassign a dept between CEOs.
  const showCeoPicker = !!actor?.is_admin_level;
  const ceos = useUsers({ role: "ceo" });

  useEffect(() => {
    if (!open) return;
    setName(department?.name ?? "");
    setDescription(department?.description ?? "");
    setCeoId(department?.ceo_id ?? null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, department?.id]);

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const input: { name: string; description?: string; ceo_id?: string | null } = {
      name: name.trim(),
      description: description.trim(),
    };
    // Only send ceo_id if the admin can see + change the field. Plain
    // CEOs / Directors don't have permission and shouldn't accidentally
    // null out the assignment.
    if (showCeoPicker) input.ceo_id = ceoId;
    const handlers = { onSuccess: () => onClose() };
    if (isEdit && department) {
      update.mutate({ id: department.id, input }, handlers);
    } else {
      create.mutate(input, handlers);
    }
  }

  const err = mutation.error as
    | { response?: { data?: { name?: string[]; detail?: string } } }
    | undefined;
  const errorMsg = err?.response?.data?.detail ?? err?.response?.data?.name?.[0];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit "${department?.name}"` : "New department"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the department's name or description. Members are not moved."
              : "Create a new organisational unit. You can assign directors and users to it from the User management page."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="df-name">Name <span className="text-destructive">*</span></Label>
            <Input
              id="df-name" required maxLength={100} autoFocus
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Climate Finance"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="df-desc">
              Description <span className="text-xs text-muted-foreground">— optional</span>
            </Label>
            <Textarea
              id="df-desc" rows={3}
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What this department is responsible for."
            />
          </div>

          {showCeoPicker && (
            <div className="space-y-1.5">
              <Label htmlFor="df-ceo">
                CEO <span className="text-xs text-muted-foreground">— optional</span>
              </Label>
              <SearchableSelect<string>
                id="df-ceo"
                value={ceoId}
                onChange={setCeoId}
                options={(ceos.data?.results ?? []).map((u) => ({
                  value: u.id,
                  label: u.full_name || u.email,
                  hint: u.email,
                }))}
                placeholder="— No CEO assigned —"
                emptyHint="No active CEOs found."
                disabled={ceos.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                One CEO per department. The same CEO can lead more than one.
              </p>
            </div>
          )}

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
              <X className="mr-2 h-4 w-4" />Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Save className="mr-2 h-4 w-4" />{isEdit ? "Save" : "Create"}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
